import type { DataTableProps } from './types.js';

/**
 * Display presets — explicit props on `<DataTable />` always win.
 *
 * | variant    | Resolves to | Use case |
 * |------------|-------------|----------|
 * | list       | list        | Full-page lists with footer pagination |
 * | embedded   | embedded    | Drawer / wizard / detail sub-tables |
 * | preview    | embedded    | @deprecated alias — dense import grids |
 * | workbench  | list        | @deprecated alias — wide catalog grids |
 */
export type DataTableVariant = 'list' | 'embedded' | 'preview' | 'workbench';

type VariantDefaults = Pick<
  DataTableProps<unknown>,
  | 'pagination'
  | 'scrollable'
  | 'noHorizontalScroll'
  | 'fixedLayout'
  | 'resizableColumns'
  | 'density'
  | 'compactActions'
>;

const EMBEDDED_DEFAULTS: VariantDefaults = {
  pagination: false,
  scrollable: true,
};

const LIST_DEFAULTS: VariantDefaults = {};

const VARIANT_DEFAULTS: Record<DataTableVariant, VariantDefaults> = {
  list: LIST_DEFAULTS,
  embedded: EMBEDDED_DEFAULTS,
  preview: {
    ...EMBEDDED_DEFAULTS,
    scrollable: false,
    noHorizontalScroll: true,
    fixedLayout: true,
    density: 'compact',
    compactActions: true,
  },
  workbench: {
    ...LIST_DEFAULTS,
    fixedLayout: true,
  },
};

export function mergeDataTableVariantProps<T>(
  variant: DataTableVariant | undefined,
  props: DataTableProps<T>,
): DataTableProps<T> & { variant?: DataTableVariant } {
  if (!variant || variant === 'list') return props;
  const defaults = VARIANT_DEFAULTS[variant];
  return {
    ...defaults,
    ...props,
    variant,
    density: props.density ?? defaults.density,
    compactActions: props.compactActions ?? defaults.compactActions,
    pagination: props.pagination ?? defaults.pagination,
    scrollable: props.scrollable ?? defaults.scrollable,
    noHorizontalScroll: props.noHorizontalScroll ?? defaults.noHorizontalScroll,
    fixedLayout: props.fixedLayout ?? defaults.fixedLayout,
  };
}
